/**
 * Wire format for a Settings → Backup & Transfer bundle.
 *
 * Deliberately dumb JSON: a `groups` map keyed by group id, each holding a map
 * keyed by the renderer `localStorage` store key, each holding a partial of
 * that store's persisted payload. That 1:1 mapping to
 * `settingsBundleManifest.ts` is what lets the guard assert "every key in this
 * file is one the manifest declared" instead of trusting the collector.
 *
 * `metaInfo` is the second storage layer — main-process
 * `{userData}/metainfo/{connectionId}/{database}.json` records fetched over
 * `metainfo:list-all`. Optional and opt-in.
 */
import type { SettingsBundleGroupId } from './settingsBundleManifest';

/** A partial of one store's persisted payload. Values are plain JSON. */
export type StoreSlicePayload = Record<string, unknown>;

/** One export group's payload: store key → the fields taken from that store. */
export type GroupPayload = Record<string, StoreSlicePayload>;

export const SETTINGS_BUNDLE_KIND = 'jam-sql-studio-settings';
export const SETTINGS_BUNDLE_VERSION = 1;

export type SettingsBundle = {
  kind: typeof SETTINGS_BUNDLE_KIND;
  /** Bumped only on a breaking wire-format change. Readers reject anything higher. */
  version: number;
  exportedAt: string;
  /** Informational only — never gates the import. */
  appVersion?: string;
  groups: Partial<Record<SettingsBundleGroupId, GroupPayload>>;
  /** Opt-in MetaInfo bundle. Records re-attach by connection id (see the UI caveat). */
  metaInfo?: Array<Record<string, unknown>>;
};

/** Non-fatal note produced while reading an untrusted file. */
export type BundleWarning = { code: string; message: string };

export type ParsedBundle =
  | { ok: true; bundle: SettingsBundle; warnings: BundleWarning[] }
  | { ok: false; error: string };
